import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useOrg } from "@/lib/orgContext";
import { Button } from "@/components/ui/button";
import { Sparkles, PlayCircle, RefreshCcw, Compass, ChevronDown } from "lucide-react";
import { DEMO_TOUR_REPLAY_EVENT } from "@/components/DemoTour";
import { readSalesPresenterCookie } from "@/lib/salesPresenter";
import { useOnboarding } from "@/lib/onboarding";
import { TOUR_TRACKS, TRACK_ORDER } from "@/lib/tourTracks";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

/**
 * True when the *server* says this org is the demo workspace. We deliberately
 * trust `org.isDemo` (sourced from `organizations.is_demo`) instead of the
 * legacy `VITE_DEMO_ORG_ID` env-var heuristic so a misconfigured build can't
 * accidentally treat a real customer as the demo (and vice versa).
 */
export function useDemoMode(): boolean {
  const { org } = useOrg();
  return Boolean(org?.isDemo);
}

/**
 * Read the non-httpOnly mirror cookie (`ah_demo_tier_view`) so the React
 * shell can flip to the Enterprise-Preview variant before the `/auth/org`
 * round-trip finishes. The signed httpOnly cookie remains the source of
 * truth on the server — this is purely a UX hint.
 */
function readDemoTierViewCookie(): "standard" | "enterprise" | null {
  try {
    const match = document.cookie
      .split("; ")
      .find((c) => c.startsWith("ah_demo_tier_view="));
    if (!match) return null;
    const raw = decodeURIComponent(match.split("=")[1] ?? "");
    const tier = raw.split("|")[0];
    if (tier === "standard" || tier === "enterprise") return tier;
    return null;
  } catch {
    return null;
  }
}

export function DemoBanner() {
  const { org } = useOrg();
  const isDemo = Boolean(org?.isDemo);
  const [cookieTier, setCookieTier] = useState<"standard" | "enterprise" | null>(null);
  const [presenterActive, setPresenterActive] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [, setLocation] = useLocation();
  const { startTour } = useOnboarding();
  const { toast } = useToast();

  useEffect(() => {
    setCookieTier(readDemoTierViewCookie());
    setPresenterActive(readSalesPresenterCookie().active);
  }, []);

  async function handlePresenterReset() {
    if (resetting) return;
    if (!window.confirm("Reset all demo data now? This wipes the sandbox and re-seeds it.")) return;
    setResetting(true);
    try {
      const res = await fetch("/api/demo/presenter/reset", { method: "POST" });
      if (res.ok) {
        toast({ title: "Demo data reset", description: "The sandbox has been re-seeded." });
        window.setTimeout(() => window.location.reload(), 600);
      } else {
        const body = await res.json().catch(() => ({} as { error?: string }));
        toast({
          title: "Reset failed",
          description: body.error ?? `Server returned ${res.status}`,
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "Reset failed",
        description: e instanceof Error ? e.message : "Network error",
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  }

  if (!isDemo) return null;

  // The demo banner is intentionally non-dismissible (task #448) — visitors
  // must always know they are in a sandbox, including the Enterprise preview
  // variant. The previous dismiss button has been removed.

  // Enterprise-Preview tier (task #448) flips palette + copy. Reads the
  // server-trusted `org.demoTier` first; falls back to the view cookie if
  // the org payload hasn't loaded yet.
  const isEnterprise = (org?.demoTier ?? cookieTier) === "enterprise";

  // Left-aligned workspace label. The institution name is server-trusted
  // (organizations.institution_name) so the ribbon reads "DEMO WORKSPACE —
  // Rhino State University" for the sales demo org without hard-coding it.
  const workspaceName = org?.institutionName ?? org?.name ?? "Demo Workspace";

  return (
    <div
      data-testid="demo-banner"
      data-tier={isEnterprise ? "enterprise" : "standard"}
      className={`sticky top-0 z-50 flex items-center gap-3 border-b border-white/10 px-4 sm:px-6 py-2 text-sm text-white flex-shrink-0 bg-gradient-to-r ${
        isEnterprise ? "from-violet-700 to-fuchsia-700" : "from-indigo-600 to-violet-600"
      }`}
    >
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
        <Sparkles className="h-3.5 w-3.5 text-white" />
      </span>
      <p className="flex-1 min-w-0 font-semibold leading-snug truncate">
        {isEnterprise ? (
          <>
            <span className="hidden sm:inline">
              ENTERPRISE PREVIEW — {workspaceName} · full surface unlocked; data resets nightly.
            </span>
            <span className="sm:hidden">ENTERPRISE PREVIEW — {workspaceName}</span>
          </>
        ) : (
          <>
            <span className="hidden sm:inline">
              DEMO WORKSPACE — {workspaceName} · sandbox resets nightly; exports unlock after your onboarding call.
            </span>
            <span className="sm:hidden">DEMO — {workspaceName}</span>
          </>
        )}
      </p>
      <span
        className="hidden sm:inline text-xs font-medium text-white/80 flex-shrink-0"
        data-testid="demo-banner-mark"
      >
        presented by Zindeal
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="flex-shrink-0 text-white/90 hover:text-white hover:bg-white/15 text-xs gap-1.5"
            data-testid="demo-tour-menu"
          >
            <Compass className="h-3.5 w-3.5" />
            Guided tour
            <ChevronDown className="h-3 w-3 opacity-80" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Persona-narrated tracks</DropdownMenuLabel>
          {TRACK_ORDER.map((id) => {
            const t = TOUR_TRACKS[id];
            return (
              <DropdownMenuItem
                key={id}
                onClick={() => startTour(id)}
                data-testid={`demo-tour-track-${id}`}
                className="flex flex-col items-start gap-0.5"
              >
                <span className="text-sm font-medium">{t.name}</span>
                <span className="text-xs text-muted-foreground">{t.description} · ~{t.estMinutes} min</span>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              if (window.location.pathname !== "/dashboard") {
                setLocation("/dashboard");
                window.setTimeout(
                  () => window.dispatchEvent(new CustomEvent(DEMO_TOUR_REPLAY_EVENT)),
                  250,
                );
              } else {
                window.dispatchEvent(new CustomEvent(DEMO_TOUR_REPLAY_EVENT));
              }
            }}
            data-testid="demo-replay-tour"
          >
            <PlayCircle className="h-3.5 w-3.5 mr-2" />
            Replay original demo tour
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {presenterActive && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handlePresenterReset}
          disabled={resetting}
          className="flex-shrink-0 text-white/90 hover:text-white hover:bg-white/15 text-xs gap-1.5"
          data-testid="demo-presenter-reset"
          title="Wipe demo data and re-seed (sales presenter only)"
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} />
          {resetting ? "Resetting…" : "Reset demo data"}
        </Button>
      )}
      <Button
        size="sm"
        asChild
        className={`flex-shrink-0 bg-white font-semibold text-xs ${
          isEnterprise ? "text-violet-700 hover:bg-violet-50" : "text-indigo-700 hover:bg-indigo-50"
        }`}
      >
        <Link href="/sign-up">{isEnterprise ? "Talk to Sales" : "Sign Up Free"}</Link>
      </Button>
    </div>
  );
}
